'use client'
// The REAL live sidebar (WikiGutter's SideNavPanel + NestedNav) reused on P2 pages —
// 1:1 with the live wiki design because it IS the live component (Veronika 2026-07-10).
//
// Collapse fix (2026-07-17): SideNavPanel's own collapsed shell breaks inside the P2 flex
// column — the wrapper collapses to width 0 and the unfold button lands off-viewport under
// the fixed header (repro'd live: button at x=-1,y=-31 → "sidebar disappears forever").
// P2 owns the collapsed state anyway, so when collapsed we render our own sticky reveal
// pill (styled in p2.css) instead of the panel; the live component is untouched.
//
// Phones (2026-10-19): below 1024 px the nav starts as the same pill and opens over the page
// (the live panel's mobile overlay). The panel stays mounted and CSS hides it until the reader
// opens it, so nothing covers the page before the script runs. SideNavPanel folds itself on
// phones with a bare onToggleCollapse() call when it mounts; P2 ignores that call (a click on
// its fold button passes the event), otherwise every tap on the pill folded the nav again.
import React, { useState } from 'react'
import { SideNavPanel } from '@/ui/layout/Gutter/new/WikiGutter'
import NestedNav, { NestedNavItem } from '@/ui/components/NestedNav'

const isPhone = () => typeof window !== 'undefined' && window.innerWidth < 1024

export function P2SideNav({
  data,
  pathOverride,
  title = 'Stages',
  titleHref,
}: {
  data: NestedNavItem[]
  pathOverride?: string
  title?: string
  /** the title links here (handbook: the hub's name, linked to the hub) */
  titleHref?: string
}) {
  const [collapsed, setCollapsed] = useState(false) // folded by the reader (desktop)
  const [open, setOpen] = useState(false) // opened by the reader (phones)
  const label = `Show ${title.toLowerCase()} navigation`
  if (collapsed) {
    return (
      <div className="p2-livenav p2-livenav-collapsed">
        <button
          className="p2-navreveal"
          onClick={() => {
            setCollapsed(false)
            setOpen(isPhone())
          }}
          aria-label={label}
        >
          <span aria-hidden>☰</span> {title}
        </button>
      </div>
    )
  }
  return (
    <div className={open ? 'p2-livenav p2-livenav-open' : 'p2-livenav'}>
      <div className="p2-livenav-pill">
        <button className="p2-navreveal" onClick={() => setOpen((o) => !o)} aria-label={label} aria-expanded={open}>
          <span aria-hidden>☰</span> {title}
        </button>
      </div>
      <div
        className="p2-livenav-panel"
        onClick={(e) => {
          // a link followed from the open overlay (often an anchor on this page) closes it
          if (open && isPhone() && (e.target as HTMLElement).closest('a')) setOpen(false)
        }}
      >
        <SideNavPanel
          title={title}
          titleHref={titleHref}
          isCollapsed={false}
          onToggleCollapse={(e?: unknown) => {
            if (!e) return // the panel's own fold-on-mount on phones, not the reader
            if (isPhone()) setOpen(false)
            else setCollapsed(true)
          }}
        >
          <NestedNav data={data} pathOverride={pathOverride} />
        </SideNavPanel>
      </div>
    </div>
  )
}
